import { FC, useState, useEffect } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

export interface SpecEditorSaveResult {
  name: string
  subtitle: string
  overwrite: boolean
}

interface SpecEditorSaveDialogProps {
  open: boolean
  /** Bare table name (no path, no .cbt), or '' for a brand-new table. */
  originalName: string
  /**
   * Whether overwrite-in-place can be offered at all — #968 Part B: only the
   * user's own tables. Exec tables and brand-new tables never show the toggle.
   */
  allowOverwrite: boolean
  saving?: boolean
  onSave: (result: SpecEditorSaveResult) => void
  onCancel: () => void
}

/**
 * The Edit Data overlay's save dialog (#968 Part B).
 *
 * Deliberately simpler than the Tables page's SaveTableDialog: there is no
 * destination picker (My Tables vs Exec) and no folder field — inside the
 * overlay, the destination folder is decided for the user (the dashboard's
 * own subfolder), by resolveTableSaveRules. This dialog only ever asks what
 * the fork-vs-overwrite rules leave open: the name, an optional subtitle,
 * and — when the table is the user's own — whether to overwrite it.
 */
export const SpecEditorSaveDialog: FC<SpecEditorSaveDialogProps> = ({
  open,
  originalName,
  allowOverwrite,
  saving = false,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(originalName || 'New Table')
  const [subtitle, setSubtitle] = useState('')
  const [overwrite, setOverwrite] = useState(allowOverwrite)

  // Reset state whenever the dialog opens — it may open again for a
  // different table if the user cancels and reopens Edit Data.
  useEffect(() => {
    if (open) {
      setName(originalName || 'New Table')
      setSubtitle('')
      setOverwrite(allowOverwrite)
    }
  }, [open, originalName, allowOverwrite])

  const handleOverwriteChange = (checked: boolean) => {
    setOverwrite(checked)
    if (checked) {
      setName(originalName || 'New Table')
    } else if (name === originalName) {
      setName(`${originalName} (Edited)`)
    }
  }

  const handleSave = () => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      subtitle: subtitle.trim(),
      overwrite: allowOverwrite && overwrite,
    })
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Save Table</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!allowOverwrite && (
            <Typography variant="body2" color="text.secondary">
              {originalName
                ? 'This table isn\'t yours to overwrite. Please save your own copy for this dashboard.'
                : 'Saves as a new table in this dashboard\'s Tables folder.'}
            </Typography>
          )}
          {allowOverwrite && (
            <FormControlLabel
              control={
                <Switch
                  checked={overwrite}
                  onChange={(_e, checked) => handleOverwriteChange(checked)}
                  color="info"
                />
              }
              label={
                <Typography variant="body2">
                  Overwrite <strong>{originalName}</strong>
                </Typography>
              }
            />
          )}
          <TextField
            label="Table name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={allowOverwrite && overwrite}
            size="small"
            fullWidth
            autoFocus={!allowOverwrite}
          />
          <TextField
            label="Subtitle (optional)"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            size="small"
            fullWidth
            placeholder="Shows beneath the table name in the TOC"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="info"
          variant="contained"
          disabled={saving || !name.trim()}
        >
          {saving ? 'Saving...' : (allowOverwrite && overwrite) ? 'Save' : 'Save As New'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SpecEditorSaveDialog
